import { useMemo } from "react"
import PropTypes from "prop-types"
import { TextField, Autocomplete } from "@mui/material"
import { replace, toLower, entries, reduce, get } from "lodash"
import DateFormPicker from "components/date-form-picker"

export default function Field({ type, id, label, on, options, optionLabels, required, sort, formData, setFormData, touch, isTouched, error, validations }) {
  const showError = isTouched && !!error
  const errorText = showError ? error : null

  const sortedOptions = useMemo(() => {
    if (!sort) return options
    return reduce(entries(optionLabels).sort(sort), (acc, [k]) => [...acc, k], [])
  }, [options, optionLabels, sort])

  switch (type) {
    case "string":
    case "text":
    case "email":
      return <TextField
        variant="standard"
        required={required}
        id={id}
        name={id}
        label={label}
        value={formData[id] || ""}
        onChange={event => {
          setFormData({
            [event.target.name]: event.target.value,
          })
        }}
        onBlur={() => touch(id)}
        error={showError}
        helperText={errorText}
        multiline={type === "text"}
      />
    case "alias": {
      const value = replace(toLower(formData[on] || ""), /[\s-]/g, "_")
      return <TextField variant="standard" disabled id={id} name={id} label={label} value={value} />
    }
    case "select":
      return <Autocomplete
        value={formData[id] || null}
        id={id}
        name={id}
        options={sortedOptions}
        getOptionLabel={(id) => optionLabels[id]}
        renderInput={(params) => <TextField
          variant="standard"
          onBlur={() => touch(id)}
          name={id}
          {...params}
          label={label}
          error={showError}
          helperText={errorText}
          required={required}
        />}
        onChange={(_, selected) => {
          setFormData({
            [id]: selected,
          })
        }}
      />
    case "multi":
      return <Autocomplete
        multiple
        value={formData[id] || []}
        id={id}
        name={id}
        options={sortedOptions}
        getOptionLabel={(id) => optionLabels[id]}
        renderInput={(params) => <TextField
          variant="standard"
          name={id}
          {...params}
          label={label}
          error={showError}
          helperText={errorText}
          onBlur={() => touch(id)}
          required={required}
        />}
        onChange={(_, selected) => {
          setFormData({
            [id]: selected,
          })
        }}
      />

    case "date":
      return <DateFormPicker
        {...{
          id,
          onChange: (date) => { setFormData({ [id]: date }) },
          onBlur: () => { touch(id) },
          keepMoment: true,
          label,
          earliestDate: get(validations, "minDate"),
          clearable: !required,
          ...{ selectedDate: formData[id] ?? null },
          helperText: errorText,
        }}
      />

    default:
      throw new Error("unsupported form")
  }
}

Field.propTypes = {
  type: PropTypes.oneOf(["string", "text", "email", "alias", "select", "multi", "date"]).isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  on: PropTypes.string,
  options: PropTypes.array,
  optionLabels: PropTypes.objectOf(PropTypes.string),
  required: PropTypes.bool.isRequired,
  sort: PropTypes.func,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  touch: PropTypes.func.isRequired,
  isTouched: PropTypes.bool.isRequired,
  error: PropTypes.string,
  validations: PropTypes.shape({
    isRequired: PropTypes.bool,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
    minDate: PropTypes.string,
  }),
}
